import { ReactNode, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useEntitlement } from '../hooks/useEntitlement';

/* ============================================================
   RequirePro
   ------------------------------------------------------------
   Server-style gate around the measurement routes. Even if a
   user crafts the URL `/app/angle` directly (deep-link, PWA
   bookmark, App-Review reviewer poking at navigation) we want
   the Pro entitlement to be the unforgeable line between free
   and paid.

   Behaviour:
     • Pro user → render children unchanged.
     • Non-Pro user, entitlement still loading → render nothing
       for one tick to avoid a flash of the paywall before the
       real state arrives.
     • Non-Pro user, confirmed → redirect to /app/account with
       a hash flag the AccountSettingsPage can read to auto-open
       the paywall sheet.

   Pairs with the HomePage gating that catches taps before
   navigation happens; this is the defence-in-depth layer that
   catches anything else.
   ============================================================ */

interface Props {
  children: ReactNode;
}

export function RequirePro({ children }: Props) {
  const { isPro, loading } = useEntitlement();

  // Flash-protection: while we're still resolving CustomerInfo we
  // render nothing rather than the paywall. The hook re-fires the
  // moment the SDK answers, so this is at most a single React tick.
  if (loading) return null;

  if (!isPro) {
    return <Navigate to="/app/account#paywall" replace />;
  }

  return <>{children}</>;
}

/* Helper for AccountSettingsPage to detect the deep-link and
   auto-open the paywall sheet. Lives next to the gate so any
   change to the hash convention only touches one file. */
export const PAYWALL_DEEPLINK_HASH = '#paywall';

export function useShouldAutoOpenPaywall(): boolean {
  // Re-evaluate on every render so React Router's hash changes
  // are picked up — the hook is cheap.
  if (typeof window === 'undefined') return false;
  return window.location.hash === PAYWALL_DEEPLINK_HASH;
}

/* Clear the hash after the paywall has been opened so a second
   visit to /app/account doesn't auto-reopen it. */
export function useClearPaywallDeeplinkOnMount(active: boolean) {
  useEffect(() => {
    if (!active) return;
    if (typeof window === 'undefined') return;
    if (window.location.hash !== PAYWALL_DEEPLINK_HASH) return;
    // History API replace — no reload, no scroll-jump.
    const { pathname, search } = window.location;
    window.history.replaceState(null, '', `${pathname}${search}`);
  }, [active]);
}
